export type RawTileConfig = Record<string, [number, number, number, number]>;

export type TileBounds = {
  left: number;
  right: number;
  bottom: number;
  top: number;
};

export type TileZoomInfo = {
    zoomLevel: number;
    angleDegrees: number;
};

export type TileCoordinates = {
  zoom: number;
  x: number;
  y: number;
};

export type TileConfigEntry = TileCoordinates & {
  url: string;
  bounds: TileBounds;
};

export type ParsedTileConfig = {
  entries: TileConfigEntry[];
  // Keyed as `${zoom}/${x}/${y}` for quick lookup
  byZxy: Map<string, TileConfigEntry>;
  zoomInfos: Map<number, TileZoomInfo>;
};

function parseZxyFromPath(path: string): TileCoordinates | null {
  // Expect segments ending with /zoom/x/y.(png|jpg|webp|jpeg|avif|*)
  // Example: jak.../1/0/0.webp
  const normalized = path.replace(/\\+/g, "/");
  const parts = normalized.split("/");
  if (parts.length < 3) return null;
  const last = parts[parts.length - 1];
  const yStr = last.replace(/\.[^/.]+$/, "");
  const xStr = parts[parts.length - 2];
  const zStr = parts[parts.length - 3];

  const zoom = Number(zStr);
  const x = Number(xStr);
  const y = Number(yStr);

  if (!Number.isFinite(zoom) || !Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  return { zoom, x, y };
}

function boundsFromArray(arr: [number, number, number, number]): TileBounds | null {
  if (
    !Array.isArray(arr) ||
    arr.length !== 4 ||
    arr.some((v) => typeof v !== "number" || !Number.isFinite(v))
  ) {
    return null;
  }
  // Input order as provided: [left, right, bottom, top]
  const [left, right, bottom, top] = arr;
  return { left, right, bottom, top };
}

export function parseTileConfig(raw: RawTileConfig): ParsedTileConfig {
  const entries: TileConfigEntry[] = [];
  const byZxy = new Map<string, TileConfigEntry>();

  let zoomInfos: Map<number, TileZoomInfo> = new Map();

  for (const [url, boundsArray] of Object.entries(raw)) {
    const coords = parseZxyFromPath(url);
    const bounds = boundsFromArray(boundsArray);
    if (!coords || !bounds) {
      continue;
    }

    const entry: TileConfigEntry = {
      url,
      zoom: coords.zoom,
      x: coords.x,
      y: coords.y,
      bounds,
    };
    entries.push(entry);
    byZxy.set(`${entry.zoom}/${entry.x}/${entry.y}`, entry);

    if (!zoomInfos.has(entry.zoom)) {
      zoomInfos.set(entry.zoom, { zoomLevel: entry.zoom, angleDegrees: entry.bounds.top - entry.bounds.bottom });
    }

  }

  return {
    entries,
    byZxy,
    zoomInfos,
  };
}

export function getEntry(parsed: ParsedTileConfig, z: number, x: number, y: number): TileConfigEntry | undefined {
  return parsed.byZxy.get(`${z}/${x}/${y}`);
}


